import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { createPortal } from 'react-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import {
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ImageOff,
  Info,
  ShoppingCart,
  Tag,
  X,
  XCircle,
  ZoomIn,
} from 'lucide-react'
import toast from 'react-hot-toast'
import Seo from '../../components/Seo'
import PageLoader from '../../components/PageLoader'
import ImageOnlySkeletonLoader from '../../components/ImageOnlySkeletonLoader'
import SparePartRelatedSparePartsSection from '../../components/spare-parts-details/SparePartRelatedSparePartsSection'
import ShortcutsSection from '../../components/ShortcutsSection'
import { DEFAULT_SHORTCUTS } from '../../components/shortcutsData'
import { formatTND } from '../../lib/currency'
import { addToCart } from '../../lib/cart'
import { useI18n } from '../../context/I18nContext'
import { useAuth } from '../../context/AuthContext'
import { getImageUrl, normalizeImageList } from '../../lib/imageData'
import { getActivePromo, getEffectivePrice, getPromoFlashColor, getPromoToneClasses } from '../../lib/promo'
import { shouldShowPrices } from '../../lib/priceVisibility'
import { getLanguageFromCookie } from '../../lib/language'
import { getRequestOrigin } from '../../lib/requestOrigin'
import { absoluteUrl, getSiteUrl } from '../../lib/seo'

const PENDING_CART_KEY = 'etc_pending_cart_add'

function titleCase(value = '') {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function buildSparePartDetails(sparePart, t, language) {
  const priceValue = getEffectivePrice(sparePart, language)
  const showPrices = shouldShowPrices()

  return [
    {
      key: 'category',
      label: t('sparePartDetails.category', 'Category'),
      value: String(sparePart?.category || '').trim() || t('common.general'),
      icon: Tag,
    },
    {
      key: 'price',
      label: t('sparePartDetails.price', 'Price'),
      value: showPrices ? formatTND(priceValue) : t('common.priceOnRequest', 'Price on request'),
      icon: ShoppingCart,
    },
  ]
}

async function fetchJsonOrThrow(url, options) {
  const response = await fetch(url, options)
  const raw = await response.text()
  let data = {}
  try {
    data = raw ? JSON.parse(raw) : {}
  } catch (error) {
    data = { error: raw || 'Invalid JSON response' }
  }

  if (!response.ok) {
    const error = new Error(data?.error || 'Request failed')
    error.status = response.status
    throw error
  }

  return data
}

export default function SparePartPage({ initialId = '', initialSparePart = null }) {
  const router = useRouter()
  const { t, language } = useI18n()
  const { isAuthenticated } = useAuth()
  const id = router.query?.id || initialId
  const [sparePart, setSparePart] = useState(initialSparePart || null)
  const [sparePartsCatalog, setSparePartsCatalog] = useState([])
  const [loadError, setLoadError] = useState('')
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [isMounted, setIsMounted] = useState(false)
  const imageSwiperRef = useRef(null)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!id) return
    let isCurrent = true

    Promise.all([
      fetchJsonOrThrow(`/api/spare-parts/${id}?lang=${language}`, { cache: 'no-store' }),
      fetchJsonOrThrow(`/api/spare-parts?lang=${language}&paginated=false`).catch(() => []),
    ])
      .then(([data, catalogData]) => {
        if (!isCurrent) return
        setLoadError('')
        setSparePart(data)
        setSparePartsCatalog(Array.isArray(catalogData) ? catalogData : [])
      })
      .catch((error) => {
        if (!isCurrent) return
        setSparePart(null)
        setLoadError(error?.message || t('sparePartDetails.loadFailed'))
      })

    return () => {
      isCurrent = false
    }
  }, [id, language, t])

  const sparePartImages = normalizeImageList(sparePart?.images)
  const hasImages = sparePartImages.length > 0
  const hasMultipleImages = sparePartImages.length > 1
  const activePromo = getActivePromo(sparePart, language)
  const promoTone = getPromoToneClasses(activePromo?.colorTone)
  const detailItems = sparePart ? buildSparePartDetails(sparePart, t, language) : []
  const stockCount = Number(sparePart?.stock ?? 0)
  const isInStock = stockCount > 0
  const basePrice = Number(sparePart?.price ?? 0)
  const effectivePrice = getEffectivePrice(sparePart, language)
  const hasDiscount = activePromo?.kind === 'price' && effectivePrice < basePrice
  const showPrices = shouldShowPrices()
  const siteUrl = getSiteUrl()
  const englishName = String(sparePart?.translations?.en?.name || '').trim()
  const frenchName = String(sparePart?.translations?.fr?.name || '').trim()
  const baseName = String(sparePart?.name || '').trim()
  const slugName = titleCase(String(sparePart?.slug || '').replace(/-/g, ' '))
  const displayName =
    language === 'en'
      ? englishName || (baseName && baseName !== frenchName ? baseName : '') || slugName || baseName
      : frenchName || baseName || slugName
  const englishDescription = String(sparePart?.translations?.en?.description || '').trim()
  const frenchDescription = String(sparePart?.translations?.fr?.description || '').trim()
  const baseDescription = String(sparePart?.description || '').trim()
  const displayDescription =
    language === 'en'
      ? englishDescription || (baseDescription && baseDescription !== frenchDescription ? baseDescription : '')
      : frenchDescription || baseDescription
  const categoryLabel = String(sparePart?.category || '').trim() || t('common.general')
  const relatedSpareParts = useMemo(() => {
    const currentSparePartId = String(sparePart?._id || sparePart?.id || '').trim()
    const currentCategory = String(sparePart?.category || '').trim()

    if (!currentSparePartId || !Array.isArray(sparePartsCatalog) || sparePartsCatalog.length === 0) {
      return []
    }

    const candidates = sparePartsCatalog
      .filter((item) => {
        const itemId = String(item?._id || item?.id || '').trim()
        return itemId && itemId !== currentSparePartId
      })
      .map((item) => ({
        item,
        sameCategory: currentCategory && String(item?.category || '').trim() === currentCategory ? 1 : 0,
        inStock: Number(item?.stock ?? 0) > 0 ? 1 : 0,
      }))

    // Shuffle first so a stable sort still randomizes the order within each priority tier.
    for (let index = candidates.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1))
      ;[candidates[index], candidates[swapIndex]] = [candidates[swapIndex], candidates[index]]
    }

    return candidates
      .sort((a, b) => b.sameCategory - a.sameCategory || b.inStock - a.inStock)
      .map(({ item }) => item)
      .slice(0, 3)
  }, [sparePart?._id, sparePart?.id, sparePart?.category, sparePartsCatalog])
  const seoTitle =
    displayName || categoryLabel || t('sparePartDetails.seoTitle', 'Spare part details')
  const seoDescription =
    displayDescription ||
    t(
      'sparePartDetails.seoDescription',
      'Explore spare part specifications, stock status, and purchase support.',
    )
  const sparePartPageUrl = absoluteUrl(router.asPath || `/spare-parts/${id}`, siteUrl)
  const sparePartImageUrls = sparePartImages
    .map((image) => absoluteUrl(getImageUrl(image), siteUrl))
    .filter(Boolean)
  const sparePartJsonLd = sparePart
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: displayName || sparePart.name,
        description: seoDescription,
        sku: String(sparePart?.slug || sparePart?._id || '').trim(),
        image: sparePartImageUrls,
        category: categoryLabel || undefined,
        offers: {
          '@type': 'Offer',
          url: sparePartPageUrl,
          availability: Number(sparePart?.stock ?? 0) > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: {
            '@type': 'Organization',
            name: 'ETC Company',
          },
        },
      }
    : null

  const openLightbox = useCallback(
    (index = 0) => {
      if (!hasImages) return
      setLightboxIndex(index)
      setIsLightboxOpen(true)
    },
    [hasImages],
  )

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false)
    if (imageSwiperRef.current && hasMultipleImages) {
      if (typeof imageSwiperRef.current.slideToLoop === 'function') {
        imageSwiperRef.current.slideToLoop(lightboxIndex)
      } else {
        imageSwiperRef.current.slideTo(lightboxIndex)
      }
    }
    setActiveImageIndex(lightboxIndex)
  }, [hasMultipleImages, lightboxIndex])

  const goLightboxPrev = useCallback(() => {
    if (!hasImages) return
    setLightboxIndex((prev) => (prev - 1 + sparePartImages.length) % sparePartImages.length)
  }, [hasImages, sparePartImages.length])

  const goLightboxNext = useCallback(() => {
    if (!hasImages) return
    setLightboxIndex((prev) => (prev + 1) % sparePartImages.length)
  }, [hasImages, sparePartImages.length])

  useEffect(() => {
    if (!hasImages) return
    setLightboxIndex((prev) => Math.min(prev, sparePartImages.length - 1))
  }, [hasImages, sparePartImages.length])

  useEffect(() => {
    setActiveImageIndex(0)
    if (imageSwiperRef.current) {
      imageSwiperRef.current.slideTo(0, 0)
    }
  }, [id, sparePartImages.length])

  useEffect(() => {
    if (!isLightboxOpen) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeLightbox()
        return
      }
      if (!hasMultipleImages) return
      if (event.key === 'ArrowLeft') goLightboxPrev()
      if (event.key === 'ArrowRight') goLightboxNext()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [closeLightbox, goLightboxNext, goLightboxPrev, hasMultipleImages, isLightboxOpen])

  function handleAddToCart() {
    if (!sparePart) return
    if (!isAuthenticated) {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(PENDING_CART_KEY, JSON.stringify({ ...sparePart, quantity: 1, kind: 'spare-part' }))
      }
      router.push(`/auth/login?next=${encodeURIComponent(router.asPath || `/spare-parts/${id}`)}`)
      return
    }
    addToCart(sparePart, 1, { kind: 'spare-part' })
    toast.success(`${displayName || sparePart.name} ${t('sparePartsPage.grid.addedToCart')}`)
  }

  const lightboxOverlay = isLightboxOpen && hasImages ? (
    <div className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-md">
      <button
        type="button"
        onClick={closeLightbox}
        className="absolute right-5 top-5 z-20 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white transition-all duration-300 hover:border-[var(--dash-primary)] hover:bg-[var(--dash-primary)] hover:text-[var(--dash-black)]"
        aria-label={t('sparePartDetails.closeGallery', 'Close gallery')}
      >
        <X className="h-5 w-5" />
      </button>

      {hasMultipleImages ? (
        <>
          <button
            type="button"
            onClick={goLightboxPrev}
            className="absolute left-5 top-1/2 z-20 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white transition-all duration-300 hover:border-[var(--dash-primary)] hover:bg-[var(--dash-primary)] hover:text-[var(--dash-black)]"
            aria-label={t('sparePartDetails.previousImage', 'Previous image')}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={goLightboxNext}
            className="absolute right-5 top-1/2 z-20 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white transition-all duration-300 hover:border-[var(--dash-primary)] hover:bg-[var(--dash-primary)] hover:text-[var(--dash-black)]"
            aria-label={t('sparePartDetails.nextImage', 'Next image')}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      ) : null}

      <div className="flex h-full items-center justify-center px-6 py-16 sm:px-12">
        <ImageOnlySkeletonLoader
          image={sparePartImages[lightboxIndex]}
          alt={displayName || sparePart?.name || t('sparePartDetails.imageAlt', 'Spare part image')}
          containerClassName="h-full w-full max-h-full max-w-full"
          imgClassName="h-full w-full object-contain"
          watermark
        />
      </div>
    </div>
  ) : null

  if (!sparePart && !loadError) {
    return <PageLoader label={t('sparePartDetails.loading', 'Loading spare part...')} />
  }

  if (!sparePart) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center bg-[var(--dash-blacker)] px-6 text-center text-white">
        <div className="rounded-3xl border border-[var(--dash-danger)] bg-[var(--dash-danger-bg)] px-6 py-5 text-[var(--dash-danger)]">
          {loadError || t('sparePartDetails.loadFailed')}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--dash-blacker)] text-white">
      <Seo
        title={seoTitle}
        description={seoDescription}
        keywords={{
          en: [
            displayName,
            categoryLabel,
            'spare part',
            'replacement parts',
            'warehouse equipment parts',
            'industrial components',
          ],
          fr: [
            displayName,
            categoryLabel,
            'piece de rechange',
            'piece de remplacement',
            'pieces de manutention',
            'composants industriels',
          ],
        }}
        jsonLd={sparePartJsonLd ? [sparePartJsonLd] : []}
      />
      <section className="relative bg-[var(--dash-blacker)]">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('/bg/5.webp')] bg-no-repeat bg-[size:100%_100%]" />
          <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_15%_25%,rgba(255,93,16,0.08)_0%,transparent_40%)]" />
          <div className="absolute right-0 top-0 h-[600px] w-[600px] translate-x-1/4 -translate-y-1/2 rounded-full bg-[#ff5d10]/5 blur-[120px]" />
          <div className="absolute bottom-0 left-0 h-[500px] w-[500px] -translate-x-1/4 translate-y-1/2 rounded-full bg-blue-500/5 blur-[100px]" />
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'radial-gradient(#ffffff 0.5px, transparent 0.5px)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="container relative z-10 mx-auto px-6 lg:px-10">
          <div className="pt-0 pb-28">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
            <div className="space-y-4 lg:col-span-7 lg:sticky lg:top-24 lg:self-start">
              <div className="flex flex-wrap gap-3 md:flex-nowrap md:h-[520px] lg:h-[600px]">
                {hasMultipleImages ? (
                  <div className="flex w-full flex-wrap gap-3 md:w-24 md:flex-col md:flex-nowrap">
                    {sparePartImages.map((image, index) => {
                      const imageUrl = getImageUrl(image)
                      const isActive = activeImageIndex === index
                      return (
                        <button
                          key={`${imageUrl}-${index}`}
                          type="button"
                          onClick={() => {
                            setActiveImageIndex(index)
                            if (imageSwiperRef.current) {
                              if (typeof imageSwiperRef.current.slideToLoop === 'function') {
                                imageSwiperRef.current.slideToLoop(index)
                              } else {
                                imageSwiperRef.current.slideTo(index)
                              }
                            }
                          }}
                          className={`relative h-20 min-w-[84px] flex-1 overflow-hidden rounded-2xl border transition-all duration-300 md:h-20 md:w-full md:flex-none ${
                            isActive
                              ? 'border-[var(--dash-primary)] shadow-[0_0_0_1px_rgba(var(--dash-main-rgb),0.35)]'
                              : 'border-white/10 opacity-70 hover:border-white/25 hover:opacity-100'
                          }`}
                        >
                          <ImageOnlySkeletonLoader
                            image={image}
                            alt={`${displayName || sparePart.name} ${index + 1}`}
                            loading="lazy"
                            containerClassName="h-full w-full"
                            imgClassName="h-full w-full object-cover"
                            watermark
                          />
                        </button>
                      )
                    })}
                  </div>
                ) : null}

                <div className="relative flex-1 overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.03] min-h-[320px] md:h-full">
                  {hasImages ? (
                    <>
                      <Swiper
                        spaceBetween={0}
                        slidesPerView={1}
                        loop={hasMultipleImages}
                        onSwiper={(swiper) => {
                          imageSwiperRef.current = swiper
                        }}
                        onSlideChange={(swiper) => {
                          const nextIndex = typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex
                          setActiveImageIndex(nextIndex)
                        }}
                        className="h-full"
                      >
                        {sparePartImages.map((image, index) => {
                          const imageUrl = getImageUrl(image)
                          return (
                            <SwiperSlide key={`${imageUrl}-${index}`} className="h-full">
                              <button
                                type="button"
                                onClick={() => openLightbox(index)}
                                className="group relative block h-full w-full"
                                aria-label={t('sparePartDetails.openImageGallery', 'Open image gallery')}
                              >
                                <ImageOnlySkeletonLoader
                                  image={image}
                                  alt={displayName || sparePart.name}
                                  loading={index === 0 ? 'eager' : 'lazy'}
                                  fetchPriority={index === 0 ? 'high' : 'auto'}
                                  containerClassName="h-full w-full"
                                  imgClassName="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                  watermark
                                />
                              </button>
                            </SwiperSlide>
                          )
                        })}
                      </Swiper>

                      <button
                        type="button"
                        onClick={() => openLightbox(activeImageIndex)}
                        className="absolute right-4 top-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-black/35 text-white transition-all duration-300 hover:border-[var(--dash-primary)] hover:bg-[var(--dash-primary)] hover:text-[var(--dash-black)]"
                        aria-label={t('sparePartDetails.zoomImage', 'Zoom image')}
                      >
                        <ZoomIn className="h-4 w-4" />
                      </button>

                      {hasMultipleImages ? (
                        <>
                          <div className="absolute left-4 top-4 z-10 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/35 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70 backdrop-blur">
                            <span>{String(activeImageIndex + 1).padStart(2, '0')}</span>
                            <span className="h-1 w-1 rounded-full bg-white/40" />
                            <span>{String(sparePartImages.length).padStart(2, '0')}</span>
                          </div>
                          <div className="absolute bottom-5 left-5 z-10 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => imageSwiperRef.current?.slidePrev()}
                              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-black/35 text-white transition-all duration-300 hover:border-[var(--dash-primary)] hover:bg-[var(--dash-primary)] hover:text-[var(--dash-black)]"
                              aria-label={t('sparePartDetails.previousImage', 'Previous image')}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => imageSwiperRef.current?.slideNext()}
                              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-black/35 text-white transition-all duration-300 hover:border-[var(--dash-primary)] hover:bg-[var(--dash-primary)] hover:text-[var(--dash-black)]"
                              aria-label={t('sparePartDetails.nextImage', 'Next image')}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </div>
                        </>
                      ) : null}
                    </>
                  ) : (
                    <div className="relative flex h-full min-h-[320px] items-center justify-center overflow-hidden bg-[var(--dash-blacker)] px-8 py-10 text-center md:min-h-0">
                      <div className="relative z-10 flex max-w-sm flex-col items-center gap-4">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.05] text-[var(--dash-primary)]">
                          <ImageOff className="h-7 w-7" />
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/70">
                            {categoryLabel}
                          </p>
                          <p className="text-lg font-semibold text-white">
                            {t('sparePartDetails.noImagesTitle', 'No visuals available')}
                          </p>
                          <p className="text-sm leading-relaxed text-white/55">
                            {t('sparePartDetails.noImagesDescription', 'This spare part is currently listed without uploaded images.')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-8 lg:col-span-5">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--dash-primary)]" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--dash-primary)]">
                    {categoryLabel}
                  </span>
                  {activePromo ? (
                    <span
                      className={`promo-badge-flash relative overflow-visible rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] shadow-sm ${promoTone.badge}`}
                      style={{ '--promo-flash-color': getPromoFlashColor(activePromo?.colorTone) }}
                    >
                      {activePromo.label || activePromo.title || t('common.promo', 'Promo')}
                    </span>
                  ) : null}
                </div>
                <h1 className="bg-gradient-to-r from-white via-white to-white/60 bg-clip-text text-4xl font-bold leading-tight tracking-tight text-transparent md:text-5xl">
                  {displayName}
                </h1>
                <div>
                  <span
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] ${
                      isInStock
                        ? 'border-[var(--dash-success)] bg-[var(--dash-success-bg)] text-[var(--dash-success)]'
                        : 'border-[var(--dash-danger)] bg-[var(--dash-danger-bg)] text-[var(--dash-danger)]'
                    }`}
                  >
                    {isInStock ? <Info className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                    {isInStock
                      ? t('productDetails.inStock', 'In stock')
                      : t('sparePartDetails.outOfStock', 'Out of stock')}
                  </span>
                </div>
                {displayDescription ? (
                  <p className="max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
                    {displayDescription}
                  </p>
                ) : null}
                {activePromo ? (
                  <div className={`max-w-xl rounded-2xl border px-4 py-4 ${promoTone.callout}`}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.22em]">
                      {activePromo.title || activePromo.label || t('common.promo', 'Promo')}
                    </p>
                    {activePromo.description ? (
                      <p className="mt-2 text-sm leading-relaxed text-white/85">{activePromo.description}</p>
                    ) : null}
                    {showPrices && hasDiscount ? (
                      <div className="mt-3 flex flex-wrap items-end gap-3">
                        <span className="text-sm font-semibold uppercase tracking-[0.16em] text-white/50 line-through">
                          {formatTND(basePrice)}
                        </span>
                        <span className="text-2xl font-black text-[var(--dash-other)]">
                          {formatTND(effectivePrice)}
                        </span>
                      </div>
                    ) : !showPrices ? (
                      <p className="mt-3 text-sm font-semibold uppercase tracking-[0.16em] text-white/70">
                        {t('common.priceOnRequest', 'Price on request')}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-white/50">
                    {t('sparePartDetails.keyDetails', 'Key details')}
                  </h3>
                  <span className="text-xs text-white/40">{t('sparePartDetails.quickOverview', 'Quick overview')}</span>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {detailItems.map((item) => {
                    const Icon = item.icon
                    return (
                      <div
                        key={item.key}
                        className="group rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-all duration-300 hover:border-[var(--dash-main)] hover:bg-white/[0.05]"
                      >
                        <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[var(--dash-primary)] opacity-60 transition-opacity md:group-hover:opacity-100">
                          <Icon className="h-4 w-4" />
                        </div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
                          {item.label}
                        </p>
                        <p className="text-lg font-semibold text-white break-words">{item.value}</p>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="space-y-4 border-t border-white/5 pt-4">
                <h3 className="text-xs font-bold uppercase tracking-widest text-white/50">
                  {t('sparePartDetails.purchaseSupportTitle', 'Purchase support')}
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex items-start gap-4">
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-[var(--dash-primary)]">
                      <CheckCircle2 className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">
                        {t('sparePartDetails.qualityCheckTitle', 'Quality Check')}
                      </h4>
                      <p className="mt-1 text-xs leading-relaxed text-white/60">
                        {t('sparePartDetails.qualityCheckDescription', 'Selected parts verified for fit, function, and dependable daily use.')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-[var(--dash-other)]">
                      <ShoppingCart className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">
                        {t('sparePartDetails.orderSupportTitle', 'Order Support')}
                      </h4>
                      <p className="mt-1 text-xs leading-relaxed text-white/60">
                        {t('sparePartDetails.orderSupportDescription', 'Need help confirming compatibility? Our team can guide you before checkout.')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleAddToCart}
                className="group flex h-16 w-full items-center justify-center gap-3 rounded-2xl bg-[var(--dash-primary)] font-bold tracking-tight text-white transition-all duration-300 hover:bg-[var(--dash-primary-hover)] hover:text-[var(--dash-black)] active:scale-[0.98]"
              >
                <span className="text-lg">{t('sparePartDetails.addToCart', 'Add to Cart')}</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-white/5 bg-[var(--dash-main)] py-24 text-white">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 opacity-80 bg-[url('/bg/2.webp')] bg-no-repeat bg-[size:100%_100%]" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.14)_0%,transparent_34%),radial-gradient(circle_at_85%_10%,rgba(255,255,255,0.08)_0%,transparent_30%),radial-gradient(circle_at_50%_85%,rgba(255,255,255,0.06)_0%,transparent_36%)]" />
        </div>
        <div className="container mx-auto">
          <SparePartRelatedSparePartsSection items={relatedSpareParts} />
        </div>
      </section>

      <ShortcutsSection items={DEFAULT_SHORTCUTS} excludeCurrent={false} />

      {isMounted && lightboxOverlay ? createPortal(lightboxOverlay, document.body) : null}
    </div>
  )
}

export async function getServerSideProps(context) {
  const initialId = String(context?.params?.id || '').trim()
  const language = getLanguageFromCookie(context?.req?.headers?.cookie) || 'fr'
  const origin = getRequestOrigin(context?.req) || 'http://localhost:3000'

  try {
    const sparePartData = await fetchJsonOrThrow(
      `${origin}/api/spare-parts/${encodeURIComponent(initialId)}?lang=${language}`,
    )

    if (!sparePartData || typeof sparePartData !== 'object' || sparePartData.error) {
      return { notFound: true }
    }

    return {
      props: {
        initialId,
        initialSparePart: sparePartData,
      },
    }
  } catch (error) {
    if (error?.status === 404) {
      return { notFound: true }
    }

    console.error('Spare part SSR load failed', {
      id: initialId,
      language,
      error: error?.message || 'Unknown error',
    })

    return {
      props: {
        initialId,
      },
    }
  }
}
